import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

import {
  AsyncComponentFallbackTemplate,
  CardSecButton,
  CircularLoader,
  IBaseUIFallbackCommonOptions,
  IBaseUIErrorOptions,
} from '@keymono/design-system';
import { Text, useTranslations } from '@keymono/i18n';

/**
 * -----------------------------------------------------------------------------
 * The loading state component used in AllOrganizationsListFallback.
 */
function AllOrganizationsListLoader() {
  const t = useTranslations('Organizations.fallback.loadingState');

  return (
    <div className="flex w-full flex-col items-center justify-center py-20 text-center">
      <CircularLoader className="my-10 h-10 w-10" />
      <h1 className="flex items-center py-4 text-2xl">{t('title.loading')}</h1>
      <h1 className="flex items-center justify-center py-2 text-center text-xl font-light">
        {t('title.name')}
      </h1>
      <p className="py-10">{t('message')}</p>
    </div>
  );
}

/**
 * -----------------------------------------------------------------------------
 * Fallback shown when the app fails to retrieve the list of all organizations.
 * TODO: Add a helpful illustration, with proper recovery message.
 */
function AllOrganizationsListError({
  onRetryAction,
  error,
}: IBaseUIErrorOptions) {
  const t = useTranslations('Organizations.fallback.errorState');

  if (!error || !onRetryAction) return null;

  const { message, description } = error;

  return (
    <div className="flex w-full flex-col items-center justify-center py-20 text-center text-red-900">
      {/* // TODO: Replace this with an illustration */}
      <ExclamationCircleIcon className="my-5 h-16 w-16 text-red-700" />

      <h1 className="flex items-center py-4 text-2xl">{t('sorry')}</h1>
      {message ? (
        <>
          <h1 className="flex items-center justify-center py-2 text-center text-xl font-light">
            {message}
          </h1>
          {description ? <p /> : null}
        </>
      ) : null}

      <CardSecButton className="my-8" onClick={onRetryAction.onClick}>
        <Text text={onRetryAction.label ?? t('retry')} />
      </CardSecButton>
    </div>
  );
}

/**
 * -----------------------------------------------------------------------------
 * Wrapper component for the error state component AllOrganizationsListError.
 * This is used in the `AllOrganizationsListFallback` component.
 * AsyncComponentFallbackTemplate expects an ErrorComponent prop that returns JSX element.
 * AllOrganizationsListError return null if the error or onRetryAction props are not provided.
 * This wrapper component ensures that the error component is always rendered.
 * TODO: Figure out a way to avoid this wrapper component.
 */
function AllOrganizationsListViewError({
  error,
  onRetryAction,
}: IBaseUIErrorOptions) {
  return (
    <div className="mx-auto mb-40 flex flex-col items-center justify-center px-4 pb-10">
      <AllOrganizationsListError error={error} onRetryAction={onRetryAction} />
    </div>
  );
}

interface IAllOrganizationsListFallbackProps
  extends IBaseUIFallbackCommonOptions {}

/**
 * -----------------------------------------------------------------------------
 * This renders the fallback loading, and error states
 * Used in the `AllOrganizationsList` component.
 */
export function AllOrganizationsListFallback({
  error,
  onRetryAction,
  isLoading,
}: IAllOrganizationsListFallbackProps) {
  return (
    <AsyncComponentFallbackTemplate
      isLoading={isLoading}
      error={error}
      onRetryAction={onRetryAction}
      ErrorComponent={AllOrganizationsListViewError}
      LoadingComponent={AllOrganizationsListLoader}
    />
  );
}
